import { ButtonComponent } from '@syncfusion/ej2-react-buttons'
import { TextBoxComponent } from '@syncfusion/ej2-react-inputs'
import { DialogComponent } from '@syncfusion/ej2-react-popups'
import { FC } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { NwDocentDto } from '../../../types/NwDocentDto'
import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks'
import { NwTitle } from '../../../types/settingTypes/NwTitle'
import { Salutations } from '../../../types/settingTypes/Salutation'
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns'
import styles from './DlgCreateAddress.module.scss'
import { protectedResources } from '../../../global/config'
import { useOidcAccessToken } from '@axa-fr/react-oidc'
import { fetchDataFromApi } from '../../../services/fetchDataFromApi'
import { setSelectedDocent } from '../../../redux/docentSlice'
import useToast from '../../../components/global/NotificationToast'

interface IDlgAddressSelectorProps {
  visible: boolean | undefined
  onClose: () => void // raus ?
  onCancel: () => void
}

const DlgCreateAddress: FC<IDlgAddressSelectorProps> = ({ visible, onClose, onCancel }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<NwDocentDto>({
    defaultValues: {
      contactSalutation: undefined,
      contactTitle: undefined,
      contactForename: '',
      contactSurname: '',
      contactPhone: '',
      contactFax: '',
      contactEMail: '',
      contactMobilePhone: '',
      adrStreet: '',
      adrCountry: '',
      adrPostCode: '',
      adrTown: '',
    },
  })

  const { accessToken } = useOidcAccessToken()
  const dispatch = useAppDispatch()
  const { showToast } = useToast()
  const salutationResponse: Salutations[] | null = useAppSelector((state) => state.setting.besanList)
  const titlesResponse: NwTitle[] | null = useAppSelector((state) => state.setting.titleList)

  const requiredMsg = 'Dieses Feld ist ein Pflichtfeld.'
  const emailErrorMsg = 'Ungültige E-Mail Adresse.'

  const footerTemplate = () => {
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <ButtonComponent type={'button'} content={'Abbrechen'} onClick={onCancel} />
        <ButtonComponent isPrimary form="createAdrForm" content={'Speichern'} />
      </div>
    )
  }

  const handleClearForm = () => {
    // Resette Form values und Errors, bei Dialog close
    console.log('reset')
    reset()
  }

  // Formdaten speichern
  const onSubmit: SubmitHandler<NwDocentDto> = (formData) => {
    console.log(formData)

    const url = `${protectedResources.apiList.createOrUpdateDocent}`
    if (accessToken) {
      fetchDataFromApi(url, accessToken, 'POST', 'json', formData)
        .then((response) => {
          console.log(response)
          if (response.status === 200) {
            const returnData: NwDocentDto = response.data

            dispatch(setSelectedDocent({ contactId: returnData.adrId, fullName: returnData.fullName }))

            showToast({
              content: 'Neue Adresse wurde erfolgreich angelegt.',
              type: 'success',
            })
          } else {
            showToast({
              content: 'Beim Speichern der Adresse ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.',
              type: 'danger',
            })
          }
        })
        .finally(() => {
          // Dialog schließen
          onClose()
        })
    }
  }

  return (
    <DialogComponent close={handleClearForm} visible={visible} width={'60%'} height={'50%'} header={'Adresse anlegen'} footerTemplate={footerTemplate} isModal={true} showCloseIcon={false} closeOnEscape={false} target="#root">
      <div>
        <form id="createAdrForm" onSubmit={handleSubmit(onSubmit)}>
          <div className="formRow">
            <div>
              <DropDownListComponent
                floatLabelType="Always"
                cssClass={`formElement ${styles.required}`}
                dataSource={
                  salutationResponse
                    ? salutationResponse
                        .filter((x) => !x.company)
                        .filter((x) => x.salutation !== ' ')
                        .map((x) => x.salutation)
                    : []
                }
                {...register('contactSalutation', { required: requiredMsg })}
                placeholder="Anrede"
              />
              <p className="errorMsg">{(errors.contactSalutation?.message as string) || null}</p>
            </div>
            <DropDownListComponent placeholder="Titel" dataSource={titlesResponse ? titlesResponse.filter((x) => x.title).map((x) => x.title) : []} {...register('contactTitle')} floatLabelType="Always" />
          </div>

          <div className="formRow">
            <div>
              <TextBoxComponent placeholder="Vorname" cssClass={`${styles.required}`} floatLabelType="Always" {...register('contactForename', { required: requiredMsg })} />
              <p className="errorMsg">{(errors.contactForename?.message as string) || null}</p>
            </div>

            <div>
              <TextBoxComponent placeholder="Nachname" cssClass={`${styles.required}`} floatLabelType="Always" {...register('contactSurname', { required: requiredMsg })} />
              <p className="errorMsg">{(errors.contactSurname?.message as string) || null}</p>
            </div>
          </div>

          <div className="formRow">
            <div>
              <TextBoxComponent placeholder="Telefon" cssClass="formElement" floatLabelType="Always" {...register('contactPhone')} />
            </div>

            <div>
              <TextBoxComponent placeholder="Telefax" cssClass="formElement" floatLabelType="Always" {...register('contactFax')} />
            </div>
          </div>

          <div className="formRow">
            <div>
              <TextBoxComponent placeholder="E-Mail" cssClass={`${styles.required}`} floatLabelType="Always" {...register('contactEMail', { required: requiredMsg, pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: emailErrorMsg } })} />

              <p className="errorMsg">{(errors.contactEMail?.message as string) || null}</p>
            </div>

            <div>
              <TextBoxComponent placeholder="Mobiltelefon" cssClass="formElement" floatLabelType="Always" {...register('contactMobilePhone')} />
            </div>
          </div>

          <div className="formRow">
            <div>
              <TextBoxComponent placeholder="Straße" cssClass={`${styles.required}`} floatLabelType="Always" {...register('adrStreet', { required: requiredMsg })} />
              <p className="errorMsg">{(errors.adrStreet?.message as string) || null}</p>
            </div>
          </div>

          <div className="formRow">
            <div className="formRow">
              <div>
                <TextBoxComponent placeholder="Land" cssClass={`${styles.required}`} floatLabelType="Always" {...register('adrCountry', { required: requiredMsg })} />
                <p className="errorMsg">{(errors.adrCountry?.message as string) || null}</p>
              </div>
              <div>
                <TextBoxComponent
                  placeholder="PLZ"
                  cssClass={`${styles.required}`}
                  floatLabelType="Always"
                  {...register('adrPostCode', {
                    required: requiredMsg,
                    pattern: {
                      value: /^\d{5}$/,
                      message: 'Ungültiges PLZ-Format.',
                    },
                  })}
                />
                <p className="errorMsg">{(errors.adrPostCode?.message as string) || null}</p>
              </div>
            </div>

            <div>
              <TextBoxComponent placeholder="Ort" cssClass={`${styles.required}`} floatLabelType="Always" {...register('adrTown', { required: requiredMsg })} />
              <p className="errorMsg">{(errors.adrTown?.message as string) || null}</p>
            </div>
          </div>
        </form>
      </div>
    </DialogComponent>
  )
}

export default DlgCreateAddress
